import { ChangeEvent, useState } from 'react';

import { Box, Flex, LoadingOverlay, Stack, Text, TextInputProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';

import { useQuery } from '@tanstack/react-query';

import { personApi } from '@/shared/api/person';
import useAppStore from '@/shared/store/app.store';
import { IPerson } from '@/shared/types/person.types';
import { BottomSheet } from '@/shared/uikit/components/bottomSheet';
import { Checkbox } from '@/shared/uikit/components/checkbox/Checkbox';
import { LabelInput } from '@/shared/uikit/components/input';
import { ModalSheet } from '@/shared/uikit/components/modalSheet';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

import { IconUserPlus } from '@tabler/icons-react';

type PersonSelectorProps = {
	onChange: (personsId: number[]) => void;
} & TextInputProps;

export const PersonSelector = ({ onChange }: PersonSelectorProps) => {
	const isMobile = useAppStore((state) => state.isMobile);
	const [persons, setPersons] = useState<IPerson[]>([]);
	const [selectedPersons, setSelectedPersons] = useState<{ full_name: string; id: number }[]>([]);

	const [openedPersonSelector, { close: closePersonSelector, open: openPersonSelector }] =
		useDisclosure(false);

	const {
		data: personsData,
		isLoading,
		isPending,
	} = useQuery({
		queryKey: ['persons'],
		queryFn: () => personApi.getPersons(),
		enabled: openedPersonSelector,
		staleTime: Infinity,
	});

	const handleSearchPlace = (event: ChangeEvent<HTMLInputElement>) => {
		const searchValue = event.currentTarget.value.toLowerCase().trim();

		if (searchValue.length && personsData) {
			const searched = personsData.filter((person) =>
				person.full_name
					.replace(/\s\|\s[\d\D]+/gm, '')
					.toLowerCase()
					.match(searchValue),
			);

			if (searched) {
				setPersons(searched);
			}
			return;
		}
		setPersons([]);
	};

	const handleSelectPerson = (full_name: string, personId: number) => {
		const isSelected = selectedPersons.some(({ id }) => id === personId);
		if (isSelected) {
			const removed = [...selectedPersons].filter(({ id }) => id !== personId);
			return setSelectedPersons(removed);
		}

		setSelectedPersons([...selectedPersons, { full_name, id: personId }]);
	};

	const handleClose = () => {
		setSelectedPersons([]);
		onChange([]);
		closePersonSelector();
	};

	const handleSave = () => {
		onChange(selectedPersons.map(({ id }) => id));
		closePersonSelector();
	};

	const content = (
		<Box pos="relative" h="100%">
			<LoadingOverlay
				visible={isLoading || isPending}
				zIndex={1000}
				overlayProps={{ radius: 2, blur: 2 }}
			/>
			<Flex direction="column" mt={16}>
				{(persons.length ? persons : personsData && personsData)
					?.slice(0, 100)
					.map(({ id, full_name, employee_name, company_name }) => (
						<LayoutRow
							key={id}
							showDivider
							type="center"
							style={{ padding: 0, cursor: 'pointer' }}
							onClick={() => handleSelectPerson(full_name, id)}
						>
							<Flex justify="space-between" key={id}>
								<Flex direction="column" maw={300}>
									<Text size="md" fz={16} fw={600} lts="normal">
										{full_name}
									</Text>
									<Stack gap={0}>
										<Text c="dimmed" fz={14}>
											<span>{company_name}</span>
										</Text>
										<Text c="dimmed" fz={14}>
											<span>{employee_name}</span>
										</Text>
									</Stack>
								</Flex>
								<Flex align="center" gap={18}>
									<Checkbox
										checked={selectedPersons.some((selectedPerson) => selectedPerson.id === id)}
									/>
								</Flex>
							</Flex>
						</LayoutRow>
					))}
				{!isLoading && !personsData && (
					<Flex direction="column" align="center" justify="center" h={'calc(100vh - 300px)'}>
						<Text fz={18} fw={700}>
							Пока ничего нет 😔
						</Text>
					</Flex>
				)}
			</Flex>
		</Box>
	);

	if (isMobile)
		return (
			<>
				<LabelInput
					label="Участники"
					placeholder="Выбрать из списка"
					onClick={openPersonSelector}
					defaultValue={selectedPersons.map(({ full_name }) => full_name).join(', ')}
					rightSection={<IconUserPlus opacity={0.5} />}
					multiple
					readOnly
				/>

				<BottomSheet
					opened={openedPersonSelector}
					onClose={handleClose}
					trapFocus={false}
					title="Выбрать участников"
					buttons={
						selectedPersons.length
							? [
									{
										children: 'Выбрать',
										onClick: handleSave,
									},
								]
							: [
									{
										children: 'Выбрать',
										variant: 'light',
									},
								]
					}
					search={{
						onChange: handleSearchPlace,
					}}
					withBackButton={false}
					withCloseButton
					isFullScreen
				>
					{content}
				</BottomSheet>
			</>
		);

	return (
		<>
			<LabelInput
				label="Участники"
				placeholder="Выбрать из списка"
				onClick={openPersonSelector}
				defaultValue={selectedPersons.map(({ full_name }) => full_name).join(', ')}
				rightSection={<IconUserPlus opacity={0.5} />}
				multiple
				readOnly
			/>

			<ModalSheet
				opened={openedPersonSelector}
				onClose={handleClose}
				title="Выбрать участников"
				buttons={
					selectedPersons.length
						? [
								{
									children: 'Выбрать',
									onClick: handleSave,
								},
							]
						: [
								{
									children: 'Выбрать',
									variant: 'light',
								},
							]
				}
				search={{
					onChange: handleSearchPlace,
				}}
				size="lg"
				centered
			>
				<Box style={{ overflowY: 'auto' }} h="60dvh" pr="md">
					{content}
				</Box>
			</ModalSheet>
		</>
	);
};
